import {
  getCoachingScoreRange
} from "../entities/coachingScore.js";
function getCoachingGoalTrendModel(args) {
  const {
    dayProgress,
    gradingValueType,
    scorePath,
    startedScore,
    targetScore
  } = args;
  const [scoreMin, scoreMax] = getCoachingScoreRange(gradingValueType);
  const clampScore = (value) => clamp(value, scoreMin, scoreMax);
  const startValue = clampScore(startedScore);
  const pathValues = scorePath.map(clampScore);
  const hasCalls = pathValues.length > 0;
  const values = hasCalls ? [startValue, ...pathValues] : [startValue, startValue];
  const goalDelta = Math.max(1e-4, targetScore - startedScore);
  const chartMin = Math.max(
    scoreMin,
    Math.min(...values, startedScore - goalDelta * 0.16)
  );
  const chartMax = Math.min(
    scoreMax,
    Math.max(
      targetScore + goalDelta * 0.85,
      Math.max(...values) + goalDelta * 0.15
    )
  );
  const plottedSpan = hasCalls ? clamp(dayProgress, MIN_PLOTTED_DAY_PROGRESS, 1) : MIN_PLOTTED_DAY_PROGRESS;
  const xFractions = values.map(
    (_value, index) => index / (values.length - 1) * plottedSpan
  );
  const segmentTones = values.slice(1).map((value, index) => getSegmentTone(values[index] ?? value, value));
  return {
    chartMax,
    chartMin,
    currentScore: pathValues.at(-1) ?? null,
    // One continuous series (all points non-null) lets the interactive and
    // static charts interpolate one smooth line/area across tone changes. The
    // horizontal gradient from `segmentTones` supplies per-segment color.
    rows: values.map((value, index) => ({
      x: xFractions[index] ?? 0,
      value
    })),
    segmentTones,
    terminalTone: segmentTones.at(-1) ?? "flat",
    values,
    xFractions
  };
}
function getCoachingGoalTrendGradientStops(segmentTones) {
  if (segmentTones.length === 0) {
    return [
      { offset: 0, tone: "flat" },
      { offset: 1, tone: "flat" }
    ];
  }
  return segmentTones.flatMap((tone, index) => [
    { offset: index / segmentTones.length, tone },
    { offset: (index + 1) / segmentTones.length, tone }
  ]);
}
function getCoachingGoalTrendAreaFadeStops(baseValue, values) {
  const topValue = Math.max(baseValue, ...values);
  const bottomValue = Math.min(baseValue, ...values);
  const span = topValue - bottomValue;
  const baselineOffset = span <= 0 ? 1 : clamp((topValue - baseValue) / span, 0, 1);
  const stops = [];
  if (baselineOffset > 0) {
    stops.push({ offset: 0, opacity: 1 });
  }
  stops.push({ offset: baselineOffset, opacity: 0 });
  if (baselineOffset < 1) {
    stops.push({ offset: 1, opacity: 1 });
  }
  return stops;
}
function separateLabelCenters({
  a,
  b,
  minGap,
  minCenter,
  maxCenter
}) {
  const aIsUpper = a <= b;
  let upper = aIsUpper ? a : b;
  let lower = aIsUpper ? b : a;
  if (lower - upper < minGap) {
    const mid = (upper + lower) / 2;
    upper = mid - minGap / 2;
    lower = mid + minGap / 2;
  }
  if (upper < minCenter) {
    const shift = minCenter - upper;
    upper += shift;
    lower += shift;
  }
  if (lower > maxCenter) {
    const shift = lower - maxCenter;
    upper -= shift;
    lower -= shift;
  }
  return aIsUpper ? { a: upper, b: lower } : { a: lower, b: upper };
}
function fitDashArray({
  length,
  dash,
  gap
}) {
  const dashCount = Math.max(2, Math.round((length + gap) / (dash + gap)));
  const fittedGap = Math.max(0, (length - dashCount * dash) / (dashCount - 1));
  return `${dash} ${Number(fittedGap.toFixed(3))}`;
}
function getSegmentTone(previousValue, nextValue) {
  const delta = nextValue - previousValue;
  if (delta > TREND_SEGMENT_DELTA_EPSILON) {
    return "improving";
  }
  if (delta < -TREND_SEGMENT_DELTA_EPSILON) {
    return "declining";
  }
  return "flat";
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
const MIN_PLOTTED_DAY_PROGRESS = 0.08;
const TREND_SEGMENT_DELTA_EPSILON = 1e-3;
const REFERENCE_LABEL_MIN_GAP_PX = 18;
export {
  REFERENCE_LABEL_MIN_GAP_PX,
  fitDashArray,
  getCoachingGoalTrendAreaFadeStops,
  getCoachingGoalTrendGradientStops,
  getCoachingGoalTrendModel,
  separateLabelCenters
};
//# sourceMappingURL=coachingGoalTrend.js.map
